import type { ConfigLayer } from '@Map/panel/types';
import {
	NON_RESTORABLE_LAYER_FIELDS,
	stripLayerFields,
} from '@Map/panel/themeFields';

/**
 * Builds the per-layer edit map `changeTheme` seeds into `pendingEditsRef`:
 * every layer starts from its resolved base values so switching to a theme that
 * does not touch a layer still reverts that layer, then the incoming theme's
 * own edits win.
 *
 * The base layers are stripped first. A *resolved* base config carries the
 * structural and runtime fields the theme pipeline must never treat as an edit —
 * most importantly `requestToken`, an auth token that rides on the incoming
 * config (see `ConfigAdaptor.loadExternalConfigs`). `ThemeService.processBody`
 * strips it on the way out, so an unstripped seed was never a persistence bug,
 * but it left the raw token sitting in `pendingEditsRef` for the life of the
 * session. Stripping at the seed keeps the accumulator free of anything the
 * write gate would reject anyway.
 *
 * `NON_RESTORABLE_LAYER_FIELDS` (the read-gate list) is the correct policy here
 * rather than `NON_PERSISTABLE_LAYER_FIELDS`: it keeps `layers`, and the nested
 * child tree is what `mergeThemeEditsIntoConfig` reads to revert per-child
 * styling on a rehab-action group.
 *
 * The theme's own edits are spread in as-is — they arrive from
 * `ThemeService.responseParser`, which has already applied the same list.
 */
export function buildThemeSeedLayers(
	baseConfig: ConfigLayer[] | undefined,
	configEditLayers: Record<string, Partial<ConfigLayer>>,
): Record<string, Partial<ConfigLayer>> {
	const baseLayers: Record<string, Partial<ConfigLayer>> = {};
	baseConfig?.forEach(layer => {
		if (layer.id)
			baseLayers[layer.id] = stripLayerFields(
				layer,
				NON_RESTORABLE_LAYER_FIELDS,
			);
	});

	return { ...baseLayers, ...configEditLayers };
}
